import { useState } from 'react'
import { Form, Input, Switch, Button, Space, Alert, App } from 'antd'

interface InlineLabelEditorProps {
  context: string
  fieldName: string
  currentLabel: string
  currentVisible: boolean
  onSave: (fieldName: string, newLabel: string, visible: boolean) => Promise<void>
  onCancel: () => void
  showConfirmOnHide?: boolean // Only show confirmation when hiding field (for Data Maps page)
}

export function InlineLabelEditor({
  fieldName,
  currentLabel,
  currentVisible,
  onSave,
  onCancel,
  showConfirmOnHide = false
}: InlineLabelEditorProps) {
  const { modal } = App.useApp()
  const [form] = Form.useForm()
  const [isSaving, setIsSaving] = useState(false)

  const handleFinish = async (values: { label: string; visible: boolean }) => {
    // If turning visibility OFF and confirmation is enabled, show confirmation
    if (showConfirmOnHide && currentVisible && !values.visible) {
      modal.confirm({
        title: 'Confirm - Remove Field?',
        content: (
          <div>
            <p>Turning this off will remove the <strong>{currentLabel}</strong> field.</p>
            <p>You can turn it back on on the Field Configuration page.</p>
          </div>
        ),
        okText: 'Continue',
        cancelText: 'Cancel',
        onOk: async () => {
          setIsSaving(true)
          try {
            await onSave(fieldName, values.label, values.visible)
            form.resetFields()
          } catch (err) {
            // Error handled by parent
          } finally {
            setIsSaving(false)
          }
        }
      })
      return
    }

    // No confirmation needed for other changes
    setIsSaving(true)
    try {
      await onSave(fieldName, values.label, values.visible)
      form.resetFields()
    } catch (err) {
      // Error handled by parent
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{ label: currentLabel, visible: currentVisible }}
      onFinish={handleFinish}
      style={{ width: 320, padding: 8 }}
    >
      <Form.Item
        label="Label"
        name="label"
        rules={[
          { required: true, message: 'Label is required' },
          { max: 100, message: 'Label must be 100 characters or less' }
        ]}
      >
        <Input
          autoFocus
          placeholder="Enter field label"
          onPressEnter={() => form.submit()}
        />
      </Form.Item>

      <Form.Item label="Visibility" name="visible" valuePropName="checked">
        <Switch checkedChildren="Visible" unCheckedChildren="Hidden" />
      </Form.Item>

      <Alert
        type="info"
        message="This field will appear in:"
        description={
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
            <li>Dropdowns and selectors</li>
            <li>Data tables and grids</li>
            <li>Detail views and forms</li>
          </ul>
        }
        style={{ marginBottom: 16 }}
      />

      <Form.Item style={{ marginBottom: 0 }}>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} size="small">
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={isSaving} size="small">
            Save
          </Button>
        </Space>
      </Form.Item>
    </Form>
  )
}

export default InlineLabelEditor
